import React, { useState, useRef } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import SignatureCanvas from "react-signature-canvas";
import uploadIcon from "../../assets/svg/upload_file.svg";
import closeAttachment from "../../assets/svg/close-red.svg";
import galleryIcon from "../../assets/svg/gallery_ic.svg";
import cameraIcon from "../../assets/svg/camera_ic.svg";
import { useEffect } from "react";

const PaymentModal = ({ show, onHide, onSubmit, errorMsgPaymentUpdate }) => {
  const fileInputRef = useRef(null);
  const sigRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [paidAmount, setPaidAmount] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [attachmentPreview, setAttachmentPreview] = useState(null);

  useEffect(() => {
    if (!show) {
      clearModel();
    }
  }, [show]);

  const [errorMsg, setErrorMsg] = useState({
    payment_method: "",
    paid_amount: "",
    signature: "",
    attachments: "",
  });

  const paymentMethods = [
    { label: "Cash", value: "cash", icon: "💵" },
    { label: "UPI", value: "upi", icon: "📱" },
  ];

  const clearSignature = () => sigRef?.current?.clear();

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const newAttachments = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
    }));
    setAttachments([...attachments, ...newAttachments]);
    if (newAttachments.length > 0) {
      setAttachmentPreview(newAttachments[0].preview);
    }
  };

  const removeAttachment = (index) => {
    const newAttachments = attachments.filter((_, idx) => idx !== index);
    setAttachments(newAttachments);
    if (newAttachments.length === 0) {
      setAttachmentPreview(null);
    } else {
      setAttachmentPreview(newAttachments[0].preview);
    }
  };

  const handleSubmit = async () => {
    // Convert attachments to base64
    const fileToBase64 = (file) =>
      new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
      });

    let attachmentsBase64 = [];
    try {
      attachmentsBase64 = await Promise.all(
        attachments.map((att) => fileToBase64(att.file))
      );
    } catch (err) {
      console.error("Error converting attachments to base64:", err);
    }

    // Get signature as base64
    const signatureBase64 = sigRef?.current?.toDataURL("image/png") ?? "";

    const payload = {
      payment_method: paymentMethod,
      amount: parseFloat(paidAmount) || null,
      signature: signatureBase64,
      attachments: attachmentsBase64,
    };

    console.log("Payment payload prepared:", {
      ...payload,
      attachments: `${payload.attachments.length} files`,
      signature: signatureBase64.length > 0 ? "provided" : "empty",
    });

    try {
      setIsLoading(true);
      console.log("Calling onSubmit...");
      await onSubmit(payload);
      console.log("Payment onSubmit completed");
    } catch (err) {
      console.error("Error in payment submission:", err);
      const errorData = err.response?.data?.error || {};
      console.log("Error data:", errorData);

      // Extract array values and map field names
      const formattedErrors = {};
      Object.keys(errorData).forEach((key) => {
        const errorValue = errorData[key];
        const errorMessage = Array.isArray(errorValue)
          ? errorValue[0]
          : errorValue;

        // Map backend field names to frontend field names
        if (key === "amount") {
          formattedErrors["paid_amount"] = errorMessage;
        } else {
          formattedErrors[key] = errorMessage;
        }
      });

      console.log("Formatted errors:", formattedErrors);
      setErrorMsg((prev) => ({ ...prev, ...formattedErrors }));
    } finally {
      setIsLoading(false);
      console.log("=== PAYMENT HANDLER COMPLETE ===");
    }
  };
  const clearModel = () => {
    setPaymentMethod("Cash");
    setPaidAmount("");
    setAttachments([]);
    setAttachmentPreview(null);
    clearSignature();
    setErrorMsg({});
  };
  const handleClose = () => {
    clearModel();
    onHide();
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      backdrop="static"
      size="md"
      centered
    >
      <Modal.Header closeButton className="border-bottom">
        <Modal.Title className="fw-700 fs-18">Payment</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ padding: "20px" }}>
        {/* Billing Proof Attachments */}
        <div className="mb-4">
          <label className="form-label fs-13 fw-600 text-start mb-2">
            Billing proof attachments
          </label>

          <div className="d-flex align-items-center gap-3 mb-3">
            <div
              className="d-flex align-items-center justify-content-center rounded-3"
              style={{
                width: "56px",
                height: "56px",
                backgroundColor: "#E8F1FF",
              }}
            >
              <img
                src={uploadIcon}
                alt="upload"
                style={{ width: "32px", height: "32px" }}
              />
            </div>
            <div className="flex-grow-1">
              <div className="fs-14 fw-600 textFontColor">Add images</div>
              <div className="fs-12 fw-400 text-muted">
                Attach some images for billing proof
              </div>
            </div>
            <div className="d-flex gap-2">
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="d-flex align-items-center gap-2 rounded-pill px-3"
                style={{ height: "36px" }}
              >
                <img
                  src={galleryIcon}
                  alt="gallery"
                  style={{ width: "16px", height: "16px" }}
                />
                Gallery
              </Button>
              <Button
                variant="primary"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                className="d-flex align-items-center gap-2 rounded-pill px-3"
                style={{
                  height: "36px",
                  backgroundColor: "#2C3E50",
                  borderColor: "#2C3E50",
                }}
              >
                <img
                  src={cameraIcon}
                  alt="camera"
                  style={{ width: "16px", height: "16px" }}
                />
                Camera
              </Button>
            </div>
          </div>

          {attachments.length > 0 && (
            <div className="d-flex flex-wrap gap-2">
              {attachments.map((att, idx) => (
                <div
                  key={idx}
                  className="position-relative"
                  style={{ width: 80, height: 80 }}
                >
                  <img
                    src={att.preview}
                    alt={`thumb-${idx}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: 8,
                      cursor: "pointer",
                      background: "#F5F5F5",
                      border: "1px solid #E5E5E5",
                    }}
                    onClick={() => setAttachmentPreview(att.preview)}
                  />
                  <button
                    className="btn btn-sm position-absolute"
                    style={{
                      top: -8,
                      right: -8,
                      padding: "2px 6px",
                      fontSize: "10px",
                      borderRadius: "100px",
                      border: "1px solid #E5E5E5",
                      width: "21px",
                      height: "21px",
                      background: "#FFFFFF",
                    }}
                    onClick={() => removeAttachment(idx)}
                  >
                    <img src={closeAttachment} alt="close" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileUpload}
            style={{ display: "none" }}
          />
          {errorMsgPaymentUpdate.attachments && (
            <small className="text-danger mt-1">
              {errorMsgPaymentUpdate.attachments}
            </small>
          )}
        </div>

        <div className="row">
          {/* Payment Method */}
          <div className="col-md-6 mb-3">
            <label className="form-label fs-13 fw-600 text-start mb-2">
              Payment method
            </label>
            <div className="d-flex flex-column gap-2">
              {paymentMethods.map((method) => (
                <div
                  key={method.value}
                  className="d-flex align-items-center p-3 rounded-3 cursor-pointer"
                  onClick={() => setPaymentMethod(method.value)}
                  style={{
                    border: "1px solid #E5E5E5",
                    backgroundColor:
                      paymentMethod === method.value ? "#F8FAFF" : "#FFFFFF",
                    cursor: "pointer",
                  }}
                >
                  <div className="d-flex align-items-center gap-2 flex-grow-1">
                    <span style={{ fontSize: "20px" }}>{method.icon}</span>
                    <span className="fs-14 fw-500 textFontColor">
                      {method.label}
                    </span>
                  </div>
                  <input
                    type="radio"
                    name="paymentMethod"
                    checked={paymentMethod === method.value}
                    onChange={() => setPaymentMethod(method.value)}
                    style={{ width: "20px", height: "20px" }}
                  />
                </div>
              ))}
            </div>
            {errorMsgPaymentUpdate.payment_method && (
              <small className="text-danger mt-1">
                {errorMsgPaymentUpdate.payment_method}
              </small>
            )}
          </div>

          {/* Signature */}
          <div className="col-md-6 mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <label className="form-label fs-13 fw-600 text-start mb-0">
                Signature
              </label>
              <Button
                variant="link"
                size="sm"
                onClick={clearSignature}
                className="p-0 text-decoration-none"
                style={{ fontSize: "12px", color: "#6C757D" }}
              >
                Clear
              </Button>
            </div>
            <div
              style={{
                border: "1px solid #E5E5E5",
                borderRadius: "8px",
                overflow: "hidden",
                backgroundColor: "#FAFAFA",
                height: "140px",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  pointerEvents: "none",
                  zIndex: 1,
                }}
              >
                <small className="text-muted fs-11">Sign here...</small>
              </div>
              <SignatureCanvas
                ref={sigRef}
                penColor="black"
                canvasProps={{
                  height: 140,
                  className: "sigCanvas w-100",
                  style: {
                    backgroundColor: "transparent",
                    position: "relative",
                    zIndex: 2,
                  },
                }}
              />
            </div>
            {errorMsgPaymentUpdate.signature && (
              <small className="text-danger mt-1">
                {errorMsgPaymentUpdate.signature}
              </small>
            )}
          </div>
        </div>

        {/* Paid Amount */}
        <div className="mb-3">
          <label className="form-label fs-13 fw-600 text-start mb-2">
            Paid amount
          </label>
          <div className="input-group">
            <span
              className="input-group-text"
              style={{
                backgroundColor: "#F5F5F5",
                border: "1px solid #E5E5E5",
                borderRight: "none",
              }}
            >
              ₹
            </span>
            <input
              type="text"
              className="form-control"
              placeholder="Enter amount"
              value={paidAmount}
              onChange={(e) =>
                setPaidAmount(
                  e.target.value
                    .replace(/[^0-9.]/g, "")
                    .replace(/(\..*)\./g, "$1")
                )
              }
              style={{
                borderLeft: "none",
                backgroundColor: "#FFFFFF",
              }}
            />
          </div>
          {errorMsgPaymentUpdate.amount && (
            <small className="text-danger mt-1">
              {errorMsgPaymentUpdate.amount}
            </small>
          )}
        </div>
      </Modal.Body>

      <Modal.Footer className="border-top d-flex justify-content-end gap-2">
        <Button
          variant="outline-secondary"
          onClick={handleClose}
          className="rounded-pill px-4"
          style={{ borderColor: "#D4DDE7", color: "#212129" }}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          className="rounded-pill px-4 d-flex justify-content-center align-items-center"
          disabled={isLoading}
          style={{
            backgroundColor: "#3469E7",
            borderColor: "#3469E7",
            minWidth: "100px",
          }}
        >
          {isLoading ? <Spinner animation="border" size="sm" /> : "Submit"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PaymentModal;
